import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import NavigationButtons from 'apps/employer_renewals/components/NavigationButtons'
import useAppNavigation from 'apps/employer_renewals/hooks/useAppNavigation'
import { useAllowances } from 'apps/employer_renewals/providers/AllowancesProvider'
import { useEmployer } from 'apps/employer_renewals/providers/EmployerProvider'
import { EmployerRenewalsRoute } from 'apps/employer_renewals/routes'
import { type ComplianceCalculations } from 'components/hooks/quotes/useComplianceCalculations'
import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import { Banner, TextLink, useBreakpoint } from 'slab'

import RenewalApproachOptions from './RenewalApproachOptions'
import RenewalComplianceChecks from '../RenewalComplianceChecks'

type Props = {
  isLoading: boolean
  complianceCalculations: ComplianceCalculations
}

export default function SmartAllowancesContent ({
  complianceCalculations,
  isLoading,
}: Props) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const { shouldShowQMEPage, shouldShowDependentCoveragePage, navigateTo } = useAppNavigation()
  const { employerIsALE } = useEmployer()
  const { renewalApproach, setRenewalApproach, saveAllowances, isSavingAllowances } = useAllowances()

  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    // When resuming, if we set a non-match approach, we want to show the editing mode
    if (renewalApproach !== 'match') {
      setIsEditing(true)
    }
  }, [renewalApproach])

  const isNonCompliant = !complianceCalculations.allChecksPassed
  const disableNextButton = isNonCompliant && employerIsALE
  const nextButtonTooltipText = () => {
    if (!isNonCompliant || !employerIsALE) {
      return
    }

    return 'As an Applicable Large Employer (ALE), you must select a compliant allowance strategy.'
  }

  const onClickNext = async () => {
    const isSaved = await saveAllowances()
    if (!isSaved) {
      toast.error('Failed to save allowances')
      return
    }

    if (shouldShowQMEPage) {
      navigateTo(EmployerRenewalsRoute.ALLOWANCES_QME)
    } else if (shouldShowDependentCoveragePage) {
      navigateTo(EmployerRenewalsRoute.ALLOWANCES_DEPENDENTS_COVERAGE)
    } else {
      navigateTo(EmployerRenewalsRoute.ALLOWANCES_CONFIRM)
    }
  }

  return (
    <div className='flex flex-col'>
      <AnimateHeightAndOpacity isOpen={isEditing}>
        <div className='p-6'>
          {renewalApproach === 'custom'
            ? (
              <Banner title={
                <p className='body'>
                  Heads up - you have a customized 2026 renewal quote. To make any further changes, reach out to <TextLink color='inherit' href='mailto:support@thatch.com' target='_blank'>support</TextLink>.
                </p>
              }
              />)
            : (
              <RenewalApproachOptions
                renewalApproach={renewalApproach}
                onRenewalApproachChange={setRenewalApproach}
              />
              )}
        </div>
      </AnimateHeightAndOpacity>
      <div className={clsx('flex p-6', {
        'justify-end': breakpointIsAtLeast('xs'),
        '[&>*]:flex-1': !breakpointIsAtLeast('xs'),
      })}
      >
        <NavigationButtons
          nextButtonText='Sounds good'
          isNextDisabled={disableNextButton}
          isLoading={isSavingAllowances}
          nextButtonTooltipText={nextButtonTooltipText()}
          onNext={onClickNext}
          tertiaryButtonText={isEditing ? undefined : 'See more options'}
          onClickTertiary={() => setIsEditing(!isEditing)}
        />
      </div>
      <AnimateHeightAndOpacity isOpen={isEditing}>
        <div className='p-6 border-t border-gray-200'>
          <RenewalComplianceChecks complianceCalculations={complianceCalculations} isLoading={isLoading} />
        </div>
      </AnimateHeightAndOpacity>
    </div>
  )
}
